import { ClientLoadTag, CreateLoadTagInput, UpdateLoadTagInput } from '@numeo/types-main-web-app'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import type { AppDispatch, RootState } from 'store'
import { loadTagsApi } from '../api/load-tags.api'

/**
 * One catalog for the whole app: every row, picker and settings tab reads this, so a tag is fetched once and an edit shows everywhere.
 * It is scoped to a project; a different project (logout, company switch) starts from an empty catalog.
 */
interface LoadTagsState {
    /** The project this catalog belongs to; a different project never reads it. */
    projectKey: string | null
    tags: ClientLoadTag[]
    /** True only for the first load; a refresh keeps showing the tags already loaded. */
    isLoading: boolean
    isLoaded: boolean
    hasError: boolean
    /** A list request is out. */
    isFetching: boolean
    /** The list request whose result may still be applied; a request started before a project change is not it. */
    activeRequestId: string | null
    lastFailureAt: number
    lastFetchAt: number
    /** Bumped on every local create / update / delete, so a list that was already on its way when one happened is not taken as newer than it. */
    generation: number
}

export const INITIAL_LOAD_TAGS_STATE: LoadTagsState = {
    projectKey: null,
    tags: [],
    isLoading: false,
    isLoaded: false,
    hasError: false,
    isFetching: false,
    activeRequestId: null,
    lastFailureAt: 0,
    lastFetchAt: 0,
    generation: 0,
}

/** After a failed fetch, a mounting consumer waits this long before retrying on its own, so a down API is not hammered by every row. */
const RETRY_COOLDOWN_MS = 30_000
/** Opening a picker or the Tags tab refreshes the catalog (a colleague may have changed it), but not more often than this. */
const REVALIDATE_MIN_MS = 5_000

type ThunkConfig = { state: RootState; dispatch: AppDispatch }

/** Reads the catalog; when a local change landed while the request was out, reads once more so the result cannot predate it. */
export const fetchLoadTags = createAsyncThunk<{ tags: ClientLoadTag[]; fetchedAt: number }, void, ThunkConfig>(
    'loadTags/fetch',
    async (_, { getState }) => {
        try {
            const startedAt = getState().loadTags.generation
            const tags = await loadTagsApi.list()
            return { tags: startedAt === getState().loadTags.generation ? tags : await loadTagsApi.list(), fetchedAt: Date.now() }
        } catch (cause) {
            Sentry.captureException(cause, { tags: { feature: 'load-tags', operation: 'list-catalog' } })
            throw cause
        }
    },
    { condition: (_, { getState }) => !getState().loadTags.isFetching }
)

const loadTagsSlice = createSlice({
    name: 'loadTags',
    initialState: INITIAL_LOAD_TAGS_STATE,
    reducers: {
        /** Points the catalog at a project. A different one starts empty and ignores whatever an older project's request returns. */
        projectSwitched(state, action: PayloadAction<string | null>) {
            if (state.projectKey === action.payload) return
            return { ...INITIAL_LOAD_TAGS_STATE, projectKey: action.payload, generation: state.generation + 1 }
        },
        tagCreated(state, action: PayloadAction<ClientLoadTag>) {
            state.generation++
            state.tags.push(action.payload)
        },
        tagUpdated(state, action: PayloadAction<ClientLoadTag>) {
            state.generation++
            state.tags = state.tags.map((existing) => (existing.id === action.payload.id ? action.payload : existing))
        },
        tagDeleted(state, action: PayloadAction<string>) {
            state.generation++
            state.tags = state.tags.filter((existing) => existing.id !== action.payload)
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLoadTags.pending, (state, action) => {
                state.isFetching = true
                state.activeRequestId = action.meta.requestId
                // Keep showing the tags already loaded while they are refreshed; only the first load shows a loading state.
                state.isLoading = !state.isLoaded
                state.hasError = false
            })
            .addCase(fetchLoadTags.fulfilled, (state, action) => {
                if (state.activeRequestId !== action.meta.requestId) return
                state.isFetching = false
                state.activeRequestId = null
                state.tags = action.payload.tags
                state.lastFetchAt = action.payload.fetchedAt
                state.isLoading = false
                state.isLoaded = true
                state.hasError = false
            })
            .addCase(fetchLoadTags.rejected, (state, action) => {
                if (state.activeRequestId !== action.meta.requestId) return
                state.isFetching = false
                state.activeRequestId = null
                state.lastFailureAt = Date.now()
                state.isLoading = false
                state.hasError = true
            })
    },
})

export const { projectSwitched, tagCreated, tagUpdated, tagDeleted } = loadTagsSlice.actions
export const loadTagsReducer = loadTagsSlice.reducer

export const selectLoadTags = (state: RootState) => state.loadTags

/**
 * Points the catalog at the project and loads it when it is not loaded yet (or retries after a failed load once the cooldown has passed).
 * With `revalidate`, an already loaded catalog is refreshed too, but not more often than REVALIDATE_MIN_MS.
 */
export const syncLoadTags =
    (projectKey: string | null, revalidate: boolean) =>
    (dispatch: AppDispatch, getState: () => RootState): void => {
        dispatch(projectSwitched(projectKey))
        if (!projectKey) return
        const { isLoaded, isFetching, hasError, lastFailureAt, lastFetchAt } = getState().loadTags
        if (isFetching) return
        const cooledDown = Date.now() - lastFailureAt >= RETRY_COOLDOWN_MS
        if (!isLoaded && (!hasError || cooledDown)) void dispatch(fetchLoadTags())
        else if (revalidate && isLoaded && Date.now() - lastFetchAt >= REVALIDATE_MIN_MS) void dispatch(fetchLoadTags())
    }

/*
 * Mutations are plain thunks, not createAsyncThunk: that would serialize a failure, and callers read the API error's `response` to show its message.
 * A result that comes back after the project changed is not applied to the new project's catalog.
 */
export const createLoadTag =
    (input: CreateLoadTagInput) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<ClientLoadTag> => {
        const projectAtStart = getState().loadTags.projectKey
        const tag = await loadTagsApi.create(input)
        if (getState().loadTags.projectKey === projectAtStart) dispatch(tagCreated(tag))
        return tag
    }

export const updateLoadTag =
    (tagId: string, input: UpdateLoadTagInput) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<ClientLoadTag> => {
        const projectAtStart = getState().loadTags.projectKey
        const tag = await loadTagsApi.update(tagId, input)
        if (getState().loadTags.projectKey === projectAtStart) dispatch(tagUpdated(tag))
        return tag
    }

export const deleteLoadTag =
    (tagId: string) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        const projectAtStart = getState().loadTags.projectKey
        await loadTagsApi.remove(tagId)
        if (getState().loadTags.projectKey === projectAtStart) dispatch(tagDeleted(tagId))
    }
